import Accordion from 'react-bootstrap/Accordion';
import { useRef, useState, useEffect } from 'react';

const normalizeText = (value) => String(value || '').trim();

const getShortProjectDescription = (project) => {
  const raw = normalizeText(project?.description1 || project?.description2 || '');
  if (!raw) return 'No short description available.';

  const firstSentence = raw.split(/\.\s+/)[0]?.trim() || raw;
  if (firstSentence.length <= 120) return firstSentence;
  return `${firstSentence.slice(0, 117)}...`;
};

const getTimingItems = (project) => ([
  { key: 'green', value: normalizeText(project?.green) },
  { key: 'amber', value: normalizeText(project?.amber) },
  { key: 'red', value: normalizeText(project?.red) },
]).filter((item) => item.value);


export default function ProjectList({ displayProjects, setActiveProject, pathway, restoreProjectId = '', onRestoreHandled }) {
  const [activeLevelKey, setActiveLevelKey] = useState(null);
  const accordionRef = useRef(null);
  const projectCardRefs = useRef({});

  useEffect(() => {
    function handleClickOutside(event) {
      if (accordionRef.current && !accordionRef.current.contains(event.target)) {
        setActiveLevelKey(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const projectLookup = new Map(
    (displayProjects || [])
      .filter((project) => project && project.projectId)
      .map((project) => [project.projectId, project])
  );

  const levels = [1, 2, 3, 4, 5]
    .map((levelNum) => {
      const levelKey = `level${levelNum}`;
      const levelData = pathway?.[levelKey];
      if (!levelData) return null;

      const mandatoryProjects = (levelData.mandatoryProjects || [])
        .filter((project) => project && project._id)
        .map((project) => projectLookup.get(project._id) || {
          projectId: project._id,
          title: project.title,
          label: project.title,
          description1: project.description1 || '',
          description2: project.description2 || '',
          green: project.green || null,
          amber: project.amber || null,
          red: project.red || null,
          total: project.total || null,
          isAssigned: false,
        });

      const electiveProjects = (levelData.electiveProjects || [])
        .filter((project) => project && project._id)
        .map((project) => projectLookup.get(project._id) || {
          projectId: project._id,
          title: project.title,
          label: project.title,
          description1: project.description1 || '',
          description2: project.description2 || '',
          green: project.green || null,
          amber: project.amber || null,
          red: project.red || null,
          total: project.total || null,
          isAssigned: false,
        });

      if (mandatoryProjects.length === 0 && electiveProjects.length === 0 && !levelData.title) return null;

      return {
        levelNum,
        label: levelData.title || `Level ${levelNum}`,
        minElectives: levelData.minElectives,
        mandatoryProjects,
        electiveProjects,
      };
    })
    .filter(Boolean);

  useEffect(() => {
    const targetId = String(restoreProjectId || '').trim();
    if (!targetId || levels.length === 0) return;

    let matchedLevelIdx = -1;

    levels.forEach((level, levelIdx) => {
      if (matchedLevelIdx !== -1) return;

      const mandatoryMatch = (level?.mandatoryProjects || []).some(
        (project) => String(project?.projectId || '') === targetId
      );
      if (mandatoryMatch) {
        matchedLevelIdx = levelIdx;
        return;
      }

      const electiveMatch = (level?.electiveProjects || []).some(
        (project) => String(project?.projectId || '') === targetId
      );
      if (electiveMatch) {
        matchedLevelIdx = levelIdx;
      }
    });

    if (matchedLevelIdx === -1) {
      onRestoreHandled?.();
      return;
    }

    const levelKey = String(matchedLevelIdx);
    setActiveLevelKey(levelKey);

    const focusCard = () => {
      const targetCard = projectCardRefs.current[targetId];
      if (!targetCard) return;
      targetCard.scrollIntoView({ block: 'center', behavior: 'smooth' });
      targetCard.focus();
      onRestoreHandled?.();
    };

    let secondFrame = 0;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(focusCard);
    });

    return () => {
      cancelAnimationFrame(firstFrame);
      if (secondFrame) {
        cancelAnimationFrame(secondFrame);
      }
    };
  }, [restoreProjectId, levels, onRestoreHandled]);

  const getLevelStatus = (level) => {
    const mandatoryProjects = level?.mandatoryProjects || [];
    const electiveProjects = level?.electiveProjects || [];
    const assignedProjects = [...mandatoryProjects, ...electiveProjects].filter((project) => project?.isAssigned);
    if (assignedProjects.length === 0) return null;

    const isProjectCompleted = (project) => Boolean(project?.completedAt || project?.completed === true);
    const mandatoryCompletedCount = mandatoryProjects.filter((project) => isProjectCompleted(project)).length;
    const electiveCompletedCount = electiveProjects.filter((project) => isProjectCompleted(project)).length;
    const minElectives = Number(level?.minElectives || 0);
    const electiveGoal = minElectives > 0 ? minElectives : electiveProjects.length;
    const mandatoryDone = mandatoryCompletedCount === mandatoryProjects.length;
    const electiveDone = electiveGoal === 0 ? true : electiveCompletedCount >= electiveGoal;
    const hasScheduledAttempt = assignedProjects.some((project) => Array.isArray(project.attempts)
      && project.attempts.some((attempt) => Boolean(attempt?.scheduledDate)));

    if (mandatoryDone && electiveDone) {
      return 'completed';
    }

    if (hasScheduledAttempt || mandatoryCompletedCount > 0 || electiveCompletedCount > 0) {
      return 'scheduled';
    }

    return null;
  };

  const openProjectDetails = (project) => {
    setActiveProject(project);
  };

  const renderProjectCards = (projects, type) => {
    const safeProjects = (Array.isArray(projects) ? projects : []).filter(
      (project) => project && normalizeText(project.title || project.label)
    );

    if (safeProjects.length === 0) {
      return <p className="pathway-projects-empty mb-0">No projects listed.</p>;
    }

    return (
      <div className="pathway-project-cards">
        {safeProjects.map((project, idx) => {
          const timings = getTimingItems(project);
          const hasScheduledAttempt = Array.isArray(project.attempts)
            && project.attempts.some((attempt) => Boolean(attempt?.scheduledDate));
          const isCompleted = Boolean(project?.completedAt || project?.completed === true);
          return (
            <article
              key={project.projectId || `${type}-${idx}`}
              className="pathway-project-card"
              style={{ cursor: 'pointer' }}
              onClick={() => openProjectDetails(project)}
              ref={(node) => {
                const key = String(project?.projectId || '');
                if (!key) return;
                if (node) {
                  projectCardRefs.current[key] = node;
                } else {
                  delete projectCardRefs.current[key];
                }
              }}
              tabIndex={-1}
            >
              {(hasScheduledAttempt || isCompleted) && (
                <div className="pathway-project-status-badges">
                  {hasScheduledAttempt && !isCompleted && (
                    <span className="pathway-project-status-badge pathway-project-status-badge--scheduled">
                      In Progress
                    </span>
                  )}
                  {isCompleted && (
                    <span className="pathway-project-status-badge pathway-project-status-badge--completed">
                      Completed
                    </span>
                  )}
                </div>
              )}
              <div className="pathway-project-card-top">
                <h6 className="pathway-project-title">{project.title || project.label}</h6>
                <span className={`pathway-project-type pathway-project-type--${type}`}>
                  {type === 'mandatory' ? 'Mandatory' : 'Elective'}
                </span>
              </div>
              <p className="pathway-project-description mb-2">{getShortProjectDescription(project)}</p>
              {timings.length > 0 && (
                <div className="pathway-project-timers">
                  {timings.map((timer) => (
                    <span
                      key={`${project.projectId || idx}-${timer.key}`}
                      className={`pathway-project-timer pathway-project-timer--${timer.key}`}
                      title={timer.key}
                    >
                      {timer.value}
                    </span>
                  ))}
                </div>
              )}
            </article>
          );
        })}
      </div>
    );
  };

  const renderElectives = (minElectives, electiveProjects) => {
    if (!electiveProjects || electiveProjects.length === 0) return null;
    const hasMinimum = minElectives && Number(minElectives) > 0;

    return (
      <div className="pathway-level-section pathway-level-section--electives">
        <h6 className="pathway-level-section-title">Elective Projects</h6>
        {hasMinimum && (
          <div className="pathway-electives-note">
            Complete {minElectives} of the following:
          </div>
        )}
        {renderProjectCards(electiveProjects, 'elective')}
      </div>
    );
  };

  return (
    <div ref={accordionRef}>
      {levels.length > 0 && (
        <Accordion className="pathway-levels-accordion" activeKey={activeLevelKey} onSelect={setActiveLevelKey}>
          {levels.map((level, idx) => (
            <Accordion.Item
              eventKey={String(idx)}
              key={`${pathway?._id || pathway?.name || 'pathway'}-level-${idx}`}
              className="pathway-level-accordion-item"
            >
              <Accordion.Header>
                <div className="pathway-level-accordion-head">
                  <div className="d-flex flex-column gap-1">
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <h5 className="mb-0 pathway-level-accordion-title">{level.label}</h5>
                      {getLevelStatus(level) && (
                        <span className={`pathway-project-status-badge pathway-project-status-badge--${getLevelStatus(level)}`}>
                          {getLevelStatus(level) === 'completed' ? 'Completed' : 'In Progress'}
                        </span>
                      )}
                    </div>
                    <div className="pathway-level-counts">
                      <span>{level.mandatoryProjects.length} mandatory</span>
                      <span>
                        {level.minElectives && Number(level.minElectives) > 0
                          ? `${level.minElectives} out of ${level.electiveProjects.length} elective`
                          : `${level.electiveProjects.length} elective`}
                      </span>
                    </div>
                  </div>
                </div>
              </Accordion.Header>
              <Accordion.Body>
                <section className="pathway-level-card pathway-level-card--inside">
                  {level.mandatoryProjects.length > 0 && (
                    <div className="pathway-level-section">
                      <h6 className="pathway-level-section-title">Mandatory Projects</h6>
                      {renderProjectCards(level.mandatoryProjects, 'mandatory')}
                    </div>
                  )}

                  {renderElectives(level.minElectives, level.electiveProjects)}
                </section>
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      )}
    </div>
  );
}